import React, { lazy } from 'react';
import {
    IconAlertOctagon,
    IconBellRinging,
    IconBriefcase,
    IconBuilding,
    IconBus,
    IconCalendarEvent,
    IconCalendarTime,
    IconClipboardList,
    IconCompass,
    IconCreditCard,
    IconDashboard,
    IconDatabase,
    IconDeviceAnalytics,
    IconDeviceLaptop,
    IconDeviceMobile,
    IconFlame,
    IconFolders,
    IconHeadset,
    IconId,
    IconLayoutGrid,
    IconLayoutList,
    IconLock,
    IconMapPin,
    IconMessageDots,
    IconMessageReport,
    IconNotes,
    IconPercentage,
    IconReceipt,
    IconReceiptRefund,
    IconReceiptTax,
    IconReportAnalytics,
    IconReportMoney,
    IconRoute,
    IconSettings,
    IconShield,
    IconShieldCheck,
    IconShieldLock,
    IconStar,
    IconStars,
    IconSteeringWheel,
    IconTag,
    IconTerminal2,
    IconTestPipe,
    IconTicket,
    IconUser,
    IconUserCheck,
    IconUsers,
    IconVersions,
    IconWifi,
} from '@tabler/icons';
import AirlineSeatReclineNormalIcon from '@material-ui/icons/AirlineSeatReclineNormal';
import PagePath, { PagePathIcon, PageType } from './PagePath';

// Custom wrapped icon for seat management
const IconSeat: PagePathIcon = (props: { stroke?: number; size?: string; className?: string }) => (
    <AirlineSeatReclineNormalIcon style={{ fontSize: props.size || '1.3rem' }} className={props.className} />
);

// ---- Roam Travel operations ----
const RoamDashboardPage = lazy(() => import('../Page/Roam/Dashboard/RoamDashboardPage'));
const BookingsPage = lazy(() => import('../Page/Roam/Bookings/BookingsPage'));
const BusesPage = lazy(() => import('../Page/Roam/Buses/BusesPage'));
const BusOwnersPage = lazy(() => import('../Page/Roam/BusOwners/BusOwnersPage'));
const RoutesPage = lazy(() => import('../Page/Roam/Routes/RoutesPage'));
const MasterRoutesPage = lazy(() => import('../Page/Roam/MasterRoutes/MasterRoutesPage'));
const SchedulesPage = lazy(() => import('../Page/Roam/Schedules/SchedulesPage'));
const ViewRoutesPage = lazy(() => import('../Page/Roam/ViewRoutes/ViewRoutesPage'));
const SeatManagementPage = lazy(() => import('../Page/Roam/SeatManagement/SeatManagementPage'));

// ---- People ----
const CustomersPage = lazy(() => import('../Page/Roam/Customers/CustomersPage'));
const DriversPage = lazy(() => import('../Page/Roam/Drivers/DriversPage'));
const ConductorsPage = lazy(() => import('../Page/Roam/Conductors/ConductorsPage'));
const EmployeesPage = lazy(() => import('../Page/Roam/Employees/EmployeesPage'));
const FreelancersPage = lazy(() => import('../Page/Roam/Freelancers/FreelancersPage'));
const InfluencersPage = lazy(() => import('../Page/Roam/Influencers/InfluencersPage'));

// ---- Finance ----
const CouponsPage = lazy(() => import('../Page/Roam/Coupons/CouponsPage'));
const PaymentsPage = lazy(() => import('../Page/Roam/Payments/PaymentsPage'));
const CommissionsPage = lazy(() => import('../Page/Roam/Commissions/CommissionsPage'));
const InfluencerCommissionsPage = lazy(() => import('../Page/Roam/Influencers/InfluencerCommissionsPage'));
const PlatformFeePage = lazy(() => import('../Page/Roam/PlatformFee/PlatformFeePage'));
const ExpensesPage = lazy(() => import('../Page/Roam/Expenses/ExpensesPage'));
const GSTManagementPage = lazy(() => import('../Page/Roam/GST/GSTManagementPage'));
const GSTComplianceHubPage = lazy(() => import('../Page/Roam/GST/GSTComplianceHubPage'));

// ---- Reports ----
const BusReportPage = lazy(() => import('../Page/Roam/Reports/BusReportPage'));
const BusOwnerReportPage = lazy(() => import('../Page/Roam/Reports/BusOwnerReportPage'));
const ProfitReportPage = lazy(() => import('../Page/Roam/Expenses/ProfitReportPage'));

// ---- Configuration & Legal ----
const BookingPolicyPage = lazy(() => import('../Page/Roam/BookingPolicy/BookingPolicyPage'));
const TermsPage = lazy(() => import('../Page/Roam/Terms/TermsPage'));
const ForgotPasswordPage = lazy(() => import('../Page/Roam/ForgotPassword/ForgotPasswordPage'));

// ---- Support & System ----
const CustomerCarePage = lazy(() => import('../Page/Roam/CustomerCare/CustomerCarePage'));
const VersionsPage = lazy(() => import('../Page/Roam/Versions/VersionsPage'));
const MastersPage = lazy(() => import('../Page/Roam/Masters/MastersPage'));
const BusAmenitiesPage = lazy(() => import('../Page/Roam/BusAmenities/BusAmenitiesPage'));
const ProfilePage = lazy(() => import('../Page/Roam/Profile/ProfilePage'));

// ---- Generic admin features (settings, logs, apps) ----
const LoginPage = lazy(() => import('../Page/Login/LoginPage'));
const AdminUserPage = lazy(() => import('../Page/AdminRegister/CreateAdminUserPage'));
const CreateRolePage = lazy(() => import('../Page/Role/CreateRolePage'));
const SmsReportPage = lazy(() => import('../Page/Sms/SmsReportPage'));
const OTPReportPage = lazy(() => import('../Page/OTP/OTPReportPage'));
const PaymentGatewayReportPage = lazy(() => import('../Page/payment/PaymentGatewayReportPage'));
const GSTReportPage = lazy(() => import('../Page/GST/GSTReportPage'));
const AppInstallsPage = lazy(() => import('../Page/AppInstalls/AppInstallsPage'));
const CustomerAppReviewListPage = lazy(() => import('../Page/CustomerAppReview/CustomerAppReviewListPage'));
const AppUpdateBroadcastPage = lazy(() => import('../Page/AppUpdateBroadcast/AppUpdateBroadcastPage'));
const BlockedDeviceSetupPage = lazy(() => import('../Page/BlockedDevice/BlockedDeviceSetupPage'));
const ApiCallLogReportPage = lazy(() => import('../Page/ApiCallLog/ApiCallLogReportPage'));
const ApplicationMonitorPage = lazy(() => import('../Page/ApplicationMonitor/ApplicationMonitorPage'));
const ErrorReportPage = lazy(() => import('../Page/ErrorReport/ErrorReportPage'));
const TestSessionPage = lazy(() => import('../Page/TestSession/TestSessionPage'));

const group = (label: string, icon: PagePathIcon = IconBus) =>
    new PagePath(label, undefined, icon, null, "", "", true, false, PageType.collapse);

const allPath = () => {
    var rootPath: PagePath[] = [];

    // 1. Dashboard
    rootPath.push(new PagePath("Dashboard", '/dashboard', IconDashboard, RoamDashboardPage));

    // 2. Master
    var MasterRoute = group("Master", IconDatabase);
    rootPath.push(MasterRoute);

    // 2.1 Bus Management (inside Master)
    var BusManagementRoute = group("Bus Management", IconBus);
    MasterRoute.content.push(BusManagementRoute);
    BusManagementRoute.content.push(new PagePath("Bus Types", '/masters/BusType', IconLayoutList, MastersPage));
    BusManagementRoute.content.push(new PagePath("Bus Categories", '/masters/BusCategory', IconLayoutGrid, MastersPage));
    BusManagementRoute.content.push(new PagePath("Bus Amenities", '/bus-amenities', IconWifi, BusAmenitiesPage));
    BusManagementRoute.content.push(new PagePath("Seat Management", '/seat-management', IconSeat, SeatManagementPage));
    BusManagementRoute.content.push(new PagePath("Buses & Fleet", '/buses', IconSteeringWheel, BusesPage));

    // 2.2 Bus Operators (inside Master)
    var BusOperatorsRoute = group("Bus Operators", IconId);
    MasterRoute.content.push(BusOperatorsRoute);
    BusOperatorsRoute.content.push(new PagePath("Bus Owners", '/bus-owners', IconBriefcase, BusOwnersPage));
    BusOperatorsRoute.content.push(new PagePath("Drivers", '/drivers', IconSteeringWheel, DriversPage));
    BusOperatorsRoute.content.push(new PagePath("Conductors", '/conductors', IconTicket, ConductorsPage));

    // 2.3 Route Management (inside Master)
    var RouteManagementRoute = group("Route Management", IconRoute);
    MasterRoute.content.push(RouteManagementRoute);
    RouteManagementRoute.content.push(new PagePath("Master Routes", '/master-routes', IconCompass, MasterRoutesPage));
    RouteManagementRoute.content.push(new PagePath("Manage Routes", '/routes', IconRoute, RoutesPage));

    // 2.4 Booking Management (inside Master)
    var BookingManagementRoute = group("Booking Management", IconTicket);
    MasterRoute.content.push(BookingManagementRoute);
    BookingManagementRoute.content.push(new PagePath("Booking Types", '/masters/BookingType', IconLayoutList, MastersPage));
    BookingManagementRoute.content.push(new PagePath("Bookings", '/bookings', IconCalendarEvent, BookingsPage));

    // 2.5 Staff Management (inside Master)
    var StaffManagementRoute = group("Staff Management", IconUserCheck);
    MasterRoute.content.push(StaffManagementRoute);
    StaffManagementRoute.content.push(new PagePath("Employees", '/employees', IconUser, EmployeesPage));
    StaffManagementRoute.content.push(new PagePath("Freelancers", '/freelancers', IconDeviceLaptop, FreelancersPage));
    StaffManagementRoute.content.push(new PagePath("Influencers", '/influencers', IconStars, InfluencersPage));
    StaffManagementRoute.content.push(new PagePath("Departments", '/masters/Department', IconBuilding, MastersPage));

    // 2.6 Passengers (inside Master)
    MasterRoute.content.push(new PagePath("Passengers", '/customers', IconUser, CustomersPage));

    // 2.7 Others (inside Master, formerly General Masters + Bus Schedules)
    var OthersRoute = group("Others", IconFolders);
    MasterRoute.content.push(OthersRoute);
    OthersRoute.content.push(new PagePath("Bus Schedules", '/schedules', IconCalendarTime, SchedulesPage));
    OthersRoute.content.push(new PagePath("Cities", '/masters/City', IconMapPin, MastersPage));
    OthersRoute.content.push(new PagePath("Complaint Types", '/masters/ComplaintType', IconMessageReport, MastersPage));
    OthersRoute.content.push(new PagePath("Priorities", '/masters/Priority', IconFlame, MastersPage));

    // Hidden from navbar (accessible via direct URL / action handlers / route registration)
    rootPath.push(new PagePath("Bus Amenities Master", '/masters/BusAmenity', null, MastersPage, "", "", false));
    rootPath.push(new PagePath("View Routes", '/view-routes', null, ViewRoutesPage, "", "", false));
    rootPath.push(new PagePath("Payment Methods", '/masters/PaymentMethod', null, MastersPage, "", "", false));
    rootPath.push(new PagePath("Payment Statuses", '/masters/PaymentStatus', null, MastersPage, "", "", false));
    rootPath.push(new PagePath("Booking Statuses", '/masters/BookingStatus', null, MastersPage, "", "", false));
    rootPath.push(new PagePath("Trip Types", '/masters/TripType', null, MastersPage, "", "", false));

    // 3. Finance
    var FinanceRoute = group("Finance", IconCreditCard);
    rootPath.push(FinanceRoute);
    FinanceRoute.content.push(new PagePath("Coupons", '/coupons', IconTag, CouponsPage));
    FinanceRoute.content.push(new PagePath("Payments", '/payments', IconCreditCard, PaymentsPage));
    FinanceRoute.content.push(new PagePath("Commissions", '/commissions', IconReportMoney, CommissionsPage));
    FinanceRoute.content.push(new PagePath("Influencer Commissions", '/influencer-commissions', IconReceiptTax, InfluencerCommissionsPage));
    FinanceRoute.content.push(new PagePath("Platform Fee", '/platform-management', IconPercentage, PlatformFeePage));
    FinanceRoute.content.push(new PagePath("Expenses", '/expenses-report', IconReceipt, ExpensesPage));
    FinanceRoute.content.push(new PagePath("GST Management", '/GSTManagement', IconReceiptRefund, GSTManagementPage));
    FinanceRoute.content.push(new PagePath("GST Compliance Hub", '/GSTAdminPanel', IconShieldCheck, GSTComplianceHubPage));

    // 4. Support
    var SupportRoute = group("Support", IconHeadset);
    rootPath.push(SupportRoute);
    SupportRoute.content.push(new PagePath("Customer Care", '/customer-care', IconHeadset, CustomerCarePage));
    SupportRoute.content.push(new PagePath("App Reviews", '/report/app-reviews', IconStar, CustomerAppReviewListPage));

    // 5. Reports
    var ReportRoute = group("Reports", IconReportAnalytics);
    rootPath.push(ReportRoute);

    var FleetReportsRoute = group("Fleet & Revenue", IconBus);
    ReportRoute.content.push(FleetReportsRoute);
    FleetReportsRoute.content.push(new PagePath("Bus Report", '/bus-report', IconBus, BusReportPage));
    FleetReportsRoute.content.push(new PagePath("Bus Owner Report", '/bus-owner-report', IconUsers, BusOwnerReportPage));
    FleetReportsRoute.content.push(new PagePath("Bus Profit Report", '/profit-report', IconReportMoney, ProfitReportPage));

    var AppReportsRoute = group("Apps", IconDeviceMobile);
    ReportRoute.content.push(AppReportsRoute);
    AppReportsRoute.content.push(new PagePath("App installs", '/report/app-installs', IconDeviceMobile, AppInstallsPage));

    var CommLogsRoute = group("Communications Logs", IconMessageDots);
    ReportRoute.content.push(CommLogsRoute);
    CommLogsRoute.content.push(new PagePath("SMS", '/report/sms', IconMessageDots, SmsReportPage));
    CommLogsRoute.content.push(new PagePath("OTP", '/report/otp', IconLock, OTPReportPage));

    var FinanceReportRoute = group("Finance & Compliance", IconReceiptTax);
    ReportRoute.content.push(FinanceReportRoute);
    FinanceReportRoute.content.push(new PagePath("Payment Gateway", '/report/payment-gateway', IconCreditCard, PaymentGatewayReportPage));
    FinanceReportRoute.content.push(new PagePath("GST", '/report/gst', IconReceiptTax, GSTReportPage));

    // 6. Settings
    var SettingRoute = group("Settings", IconSettings);
    rootPath.push(SettingRoute);

    var AccessSecurityRoute = group("Access & Security", IconShieldLock);
    SettingRoute.content.push(AccessSecurityRoute);
    AccessSecurityRoute.content.push(new PagePath("Admin User", '/admin', IconUsers, AdminUserPage));
    AccessSecurityRoute.content.push(new PagePath("Role Setup", '/role/create', IconShieldLock, CreateRolePage));
    AccessSecurityRoute.content.push(new PagePath("Blocked Device", '/blockeddevice', IconShield, BlockedDeviceSetupPage));
    AccessSecurityRoute.content.push(new PagePath("My Profile", '/profile', IconUser, ProfilePage));

    var AppConfigRoute = group("App Configuration", IconSettings);
    SettingRoute.content.push(AppConfigRoute);
    AppConfigRoute.content.push(new PagePath("App Versions", '/versions', IconVersions, VersionsPage));
    AppConfigRoute.content.push(new PagePath("Push Broadcast", '/appupdatebroadcast', IconBellRinging, AppUpdateBroadcastPage));
    AppConfigRoute.content.push(new PagePath("Booking Policy", '/BookingPolicyManagement', IconClipboardList, BookingPolicyPage));
    AppConfigRoute.content.push(new PagePath("Terms & Conditions", '/terms', IconNotes, TermsPage));

    var SystemLogsRoute = group("System Logs & Ops", IconTerminal2);
    SettingRoute.content.push(SystemLogsRoute);
    SystemLogsRoute.content.push(new PagePath("API Call Log", '/apicalllog', IconTerminal2, ApiCallLogReportPage));
    SystemLogsRoute.content.push(new PagePath("Application Monitor", '/appmonitor', IconDeviceAnalytics, ApplicationMonitorPage));
    SystemLogsRoute.content.push(new PagePath("Error Reports", '/errorreports', IconAlertOctagon, ErrorReportPage));
    SystemLogsRoute.content.push(new PagePath("Test Sessions", '/testsessions', IconTestPipe, TestSessionPage));

    rootPath.push(new PagePath("Login", "/login", null, LoginPage, "", undefined, false, true));
    rootPath.push(new PagePath("Forgot Password", "/forgot-password", null, ForgotPasswordPage, "", undefined, false, true));
    return rootPath;
};

export default allPath;
